export type TableSortDirection = '' | 'asc' | 'desc';
export type TableSortValue = string | number | boolean | Date | null | undefined;

export class TableSortState {
    column = '';
    direction: TableSortDirection = '';

    toggle(column: string) {
        if (this.column !== column) {
            this.column = column;
            this.direction = 'asc';
            return;
        }

        if (this.direction === 'asc') {
            this.direction = 'desc';
            return;
        }

        if (this.direction === 'desc') {
            this.column = '';
            this.direction = '';
            return;
        }

        this.direction = 'asc';
    }

    isDirection(column: string, direction: TableSortDirection) {
        return this.column === column && this.direction === direction;
    }

    getAriaSort(column: string) {
        if (this.column !== column || this.direction === '') {
            return 'none';
        }
        return this.direction === 'asc' ? 'ascending' : 'descending';
    }

    sort<T>(items: T[], valueGetter: (item: T, column: string) => TableSortValue): T[] {
        if (!Array.isArray(items) || this.column === '' || this.direction === '') {
            return items;
        }

        const directionMultiplier = this.direction === 'asc' ? 1 : -1;
        return items
            .map((item, index) => ({item, index}))
            .sort((left, right) => {
                const comparison = compareTableValues(
                    valueGetter(left.item, this.column),
                    valueGetter(right.item, this.column)
                );
                return comparison === 0
                    ? left.index - right.index
                    : comparison * directionMultiplier;
            })
            .map(entry => entry.item);
    }
}

function compareTableValues(left: TableSortValue, right: TableSortValue) {
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty || rightEmpty) {
        if (leftEmpty && rightEmpty) {
            return 0;
        }
        return leftEmpty ? 1 : -1;
    }

    const leftDate = left instanceof Date ? left.getTime() : undefined;
    const rightDate = right instanceof Date ? right.getTime() : undefined;
    if (leftDate !== undefined || rightDate !== undefined) {
        return Number(leftDate || left) - Number(rightDate || right);
    }

    if (typeof left === 'number' || typeof right === 'number') {
        const leftNumber = Number(left);
        const rightNumber = Number(right);
        if (!isNaN(leftNumber) && !isNaN(rightNumber)) {
            return leftNumber - rightNumber;
        }
    }

    if (typeof left === 'boolean' || typeof right === 'boolean') {
        return Number(Boolean(left)) - Number(Boolean(right));
    }

    return String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
}
